
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import { Product } from '@/models/accounting/Product'
import { VatCode } from '@/models/accounting/VatCode'
import { Invoice } from '@/models/accounting/Invoice'
import { InvoiceLine } from '@/models/accounting/InvoiceLine'
import { namespace } from 'vuex-class'
import { formatCurrency } from '@/utils/currency'
import Testable from '@/mixins/Testable'
import InvoiceLineStatus from '@/enums/InvoiceLineStatus'
import FinancialType from '@/enums/FinancialType'
import DependQuantityOnCargoQuantityTransshipmentDirection from '@/enums/DependQuantityOnCargoQuantityTransshipmentDirection'
import { Customer } from '@/models/accounting/Customer'
import CustomerSelector from '@/components/Accounting/CustomerSelector.vue'
import ProductSelector from '@/components/Accounting/ProductSelector.vue'

const Products = namespace('accountingProducts')
const VatCodes = namespace('accountingVatCodes')

@Component({
	computed: {
		FinancialType() {
			return FinancialType
		},
		DependQuantityOnCargoQuantityTransshipmentDirection() {
			return DependQuantityOnCargoQuantityTransshipmentDirection
		}
	},
	components: { ProductSelector, CustomerSelector }
})
export default class InvoiceLineForm extends Mixins(Testable) {
	@Prop({ required: false, default: () => new Invoice() })
	invoice!: Invoice

	@Prop({ required: true })
	invoiceLine!: InvoiceLine

	@Prop({ required: false, default: () => [] })
	otherLines!: Array<InvoiceLine>

	@Prop({ required: false, default: true })
	showVat!: boolean

	@Prop({ required: false, default: false })
	dense!: boolean

	@Prop({ required: false, default: false })
	isCombinedInvoice!: boolean

	@Prop({ required: false, default: null })
	loadedQuantity!: number | null

	@Prop({ required: false, default: null })
	unloadedQuantity!: number | null

	@Prop({ required: false, default: true })
	editableClientOrderReference!: boolean

	@Products.Getter('peekAll')
	peekAllProducts

	@Products.Mutation('cache')
	cacheProducts

	@VatCodes.Getter('peekAll')
	peekVatCodes

	initialQuantity: number | null = null
	initialPrice: number | null = null
	initialCustomer: Customer | null = null
	quantity: number | null = null
	unitPrice: number | null = null
	description: string | null = null
	selectedCustomer: Customer | null = null
	product: Product | string | null = null
	vatCode: VatCode | null = null
	priceDependentOn: Array<InvoiceLine> = []
	dependQuantityOnCargoQuantity: boolean = false
	dependQuantityOnCargoQuantityTransshipmentDirection: DependQuantityOnCargoQuantityTransshipmentDirection | null =
		null
	syncFinancialRegistration: boolean = false

	productQuery: string = ''
	productsLoading: boolean = false
	showButtons: boolean = false
	refreshKey: number = 0
	customers: Array<Customer> = []

	get products() {
		return this.peekAllProducts()
	}

	get vatCodes() {
		return this.peekVatCodes()
	}

	get isInvoiced() {
		return this.invoiceLine.status === InvoiceLineStatus.INVOICED
	}

	get dependentPrice() {
		// eslint-disable-next-line no-unused-expressions
		this.refreshKey // only mention it to trigger update when it changes
		if (!this.invoiceLine.isPriceDependentOn) return 0
		return this.invoiceLine
			.getPriceDependentOn()
			.reduce((previousValue, line, idx) => previousValue + line.total, 0)
	}

	get total() {
		const quantity = this.quantity === null || isNaN(this.quantity) ? 0 : this.quantity
		let total
		if (this.invoiceLine.isPriceDependentOn) {
			total = this.dependentPrice * (quantity / 100)
		} else {
			const unitPrice = this.unitPrice === null || isNaN(this.unitPrice) ? 0 : this.unitPrice
			total = quantity * unitPrice
		}
		return total
	}

	get formattedTotal() {
		return formatCurrency(this.total)
	}

	get formattedTotalInclVAT() {
		if (!this.vatCode) return this.formattedTotal
		return (
			formatCurrency(this.total * (1 + this.vatCode.percentage / 100)) +
			' ' +
			this.$i18n.t('invoiceCreator.incl_vat')
		)
	}

	get formattedDependentPrice() {
		return formatCurrency(this.dependentPrice)
	}

	get showSyncFinancialRegistrationMessage() {
		return (
			Number(this.quantity) !== this.initialQuantity ||
			Number(this.unitPrice) !== this.initialPrice ||
			this.selectedCustomer?.getApiId() !== this.initialCustomer?.getApiId()
		)
	}

	get isChangingQuantity() {
		return (
			Number(this.quantity) !== this.initialQuantity
			// in future it might be better to also check if the financial registration has dependQuantityOnCargoQuantity but the precededBy is not passed as a relation yet.
		)
	}

	created() {
		this.quantity = this.initialQuantity = this.invoiceLine.quantity
		this.unitPrice = this.initialPrice = (this.invoiceLine.unitPrice || 0) / 100 || 0
		this.description = this.invoiceLine.description
		this.vatCode = this.invoiceLine.getVatCode()
		this.product = this.invoiceLine.getProduct()
		if (this.product) {
			this.cacheProducts([this.product])
		} else {
			this.product = this.invoiceLine.customProduct
		}
		this.selectedCustomer = this.initialCustomer = this.invoiceLine.getCustomer()
		this.priceDependentOn = this.invoiceLine.getPriceDependentOn()
		this.dependQuantityOnCargoQuantity = this.invoiceLine.dependQuantityOnCargoQuantity
		this.dependQuantityOnCargoQuantityTransshipmentDirection =
			this.invoiceLine.dependQuantityOnCargoQuantityTransshipmentDirection
	}

	@Watch('dependQuantityOnCargoQuantityTransshipmentDirection')
	onDependQuantityOnCargoQuantityCargoQuantityTransshipmentDirectionChanged(
		newValue: DependQuantityOnCargoQuantityTransshipmentDirection
	) {
		this.invoiceLine.dependQuantityOnCargoQuantityTransshipmentDirection = newValue
		if (!this.dependQuantityOnCargoQuantityTransshipmentDirection) {
			this.quantity = this.invoiceLine.quantity
		} else {
			this.quantity =
				this.dependQuantityOnCargoQuantityTransshipmentDirection ===
				DependQuantityOnCargoQuantityTransshipmentDirection.LOADING
					? this.loadedQuantity
					: this.unloadedQuantity
		}
	}

	@Watch('dependQuantityOnCargoQuantity')
	onDependQuantityOnCargoQuantityCargoQuantityChanged(newValue: boolean) {
		this.invoiceLine.dependQuantityOnCargoQuantity = newValue
		if (!newValue) {
			this.dependQuantityOnCargoQuantityTransshipmentDirection = null
		} else {
			if (!this.dependQuantityOnCargoQuantityTransshipmentDirection) {
				this.quantity = this.invoiceLine.quantity
			} else {
				this.quantity =
					this.dependQuantityOnCargoQuantityTransshipmentDirection ===
					DependQuantityOnCargoQuantityTransshipmentDirection.LOADING
						? this.loadedQuantity
						: this.unloadedQuantity
			}
		}
	}

	@Watch('product')
	syncProduct(value: Product) {
		if (typeof value === 'string') {
			this.invoiceLine.customProduct = value
		} else {
			this.invoiceLine.setProduct(value)
		}
	}

	@Watch('selectedCustomer')
	syncCustomer(value: Customer) {
		this.invoiceLine.setCustomer(value)
	}

	@Watch('vatCode')
	syncVatCode(value: VatCode) {
		this.invoiceLine.setVatCode(value)
	}

	@Watch('quantity')
	onQuantityChanged(newValue: string | number) {
		this.invoiceLine.quantity = Number.parseFloat(newValue + '')
		if (this.dependQuantityOnCargoQuantity) this.invoiceLine.quantity = 0
		this.$emit('changePrice')
	}

	@Watch('unitPrice')
	onUnitPriceChanged(newValue: number | null) {
		if (newValue !== null) {
			this.invoiceLine.unitPrice = newValue * 100
			this.$emit('changePrice')
		}
	}

	@Watch('syncFinancialRegistration')
	syncFinancialRegistrations(newValue: boolean) {
		this.invoiceLine.syncPrecedingFinancialRegistration = newValue
	}

	@Watch('description')
	onDescriptionChanged(newValue: string | null) {
		if (newValue !== null) {
			this.invoiceLine.description = newValue
		}
	}

	@Watch('priceDependentOn')
	onPriceDependentOnChanged(value) {
		this.invoiceLine.setPriceDependentOn(value)
		// This is needed because bindings on relationships in Coloquent doesn't work always.
		this.refreshKey++
	}
}
